<template>
  <div>
    <Header>One Time No Ride</Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <div class="mx-auto my-4">
        <ValidationObserver v-slot="{ invalid, validate, errors }">
          <form name="oneTimeNoRideForm" @submit.prevent="validate().then(() => onSubmit(invalid))">
            <FormSection name="Student Information" class="my-4">
              <div class="grid grid-cols-3 gap-4"
                v-if="student !== undefined">
                <TextField
                  :disabled="true"
                  :value="student.firstName">
                  First name
                </TextField>
                <TextField
                  :disabled="true"
                  :value="student.lastName">
                  Last name
                </TextField>
                <TextField
                  :disabled="true"
                  :value="student.studentId">
                  Student ID
                </TextField>
              </div>
            </FormSection>
            <FormSection name="School, Staff, or Guardian Information" class="my-4">
              <div class="grid grid-cols-3 gap-4">
                <ValidationProvider v-if="isInternal" rules="required|max:32" name="requesterFirstName" v-slot="{ errors }">
                  <TextField
                    :required="true"
                    :disabed="false"
                    v-model="data.requester[0].first_name">
                    First name
                  </TextField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
                <ValidationProvider v-if="isInternal" rules="required|max:32" name="requesterLastName" v-slot="{ errors }">
                  <TextField
                    :required="true"
                    :disabed="false"
                    v-model="data.requester[0].last_name">
                    Last name
                  </TextField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
                <ValidationProvider rules="required_if:isInternal,true|max:32" name="requesterRelationship" v-slot="{ errors }">
                  <TextField
                    :required="isInternal"
                    :disabed="false"
                    v-model="data.requester[0].relationship">
                    Relationship
                  </TextField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
              </div>
            </FormSection>
            <FormSection name="CST Internal Information" class="my-4"
              v-if="isInternal">
              <div class="grid grid-cols-3 gap-4">
                <div class="col-span-1">
                  <div class="flex flex-col gap-2 mt-3">
                    <ValidationProvider :rules="notifiedTimeRules[notifiedLastNightSelected]" name="notifiedTime" v-slot="{ errors }">
                      <TimeField
                        :disabled="notifiedLastNightSelected"
                        v-model="data.notifiedTime">
                        Notification Received (in your local time)
                      </TimeField>
                      <span
                        class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                        name="error-text">
                        {{ errors[0] }}
                      </span>
                    </ValidationProvider>
                    <label class="pl-2 text-left block text-sm leading-6 text-gray-900">
                      <input
                        type="checkbox"
                        @change="onInputNotifiedLastNight"
                      />
                      Select if notification was before today
                    </label>
                  </div>
                </div>
                <div class="col-span-2">
                  <TextArea
                    class="h-24"
                    :required="false"
                    :disabed="false"
                    v-model="data.notes">
                    Notes
                  </TextArea>
                </div>
              </div>
            </FormSection>
            <FormSection name="No Ride Information" class="my-4">
              <div class="grid grid-cols-3 gap-x-4">
                <ValidationProvider rules="required" name="direction" v-slot="{ errors }">
                  <RadioField
                    v-model="data.no_ride[0].direction"
                    :options="directionOptions"
                    :alignment="'col'">
                    Direction
                  </RadioField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
                <div class="w-full">
                  <ValidationProvider rules="required" name="startDate" v-slot="{ errors }">
                    <CalendarField
                      :required="true"
                      v-model="data.no_ride[0].start_date"
                      :min="getTodayDate()">
                      {{ data.no_ride[0].multiple ? 'Start date' : 'Date' }}
                    </CalendarField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                  <div class="my-4">
                    <label class="text-left block text-sm leading-6 text-gray-900">
                      <input
                        type="checkbox"
                        @change="toggleMultiple"
                      />
                      Is this no ride for multiple consecutive days?
                    </label>
                  </div>
                </div>
                <div class="w-full">
                  <div v-if="data.no_ride[0].multiple">
                    <ValidationProvider rules="required" name="endDate" v-slot="{ errors }">
                      <CalendarField
                        :required="true"
                        v-model="data.no_ride[0].end_date"
                        :min="minEndDate"
                        :max="twoWeeksAhead()">
                        End date
                      </CalendarField>
                      <span
                        class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                        name="error-text">
                        {{ errors[0] }}
                      </span>
                    </ValidationProvider>
                    <p v-if="isTwoWeeks()" class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      If the student will not ride for more than 2 weeks, then submit a cancel request instead
                    </p>
                    <p v-if="beforeStart()" class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      End date should be later than start date
                    </p>
                  </div>
                </div>
              </div>
            </FormSection>
            <div class="my-4">
              <div class="w-full rounded-md bg-gray-200 p-2 my-2"
                v-if="!canSubmit">
                <span class="uppercase text-gray-500">
                  Missing Permissions to Submit Form
                </span>
              </div>
              <div class="flex flex-row">
                <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  name="error-text"
                  v-if="displayFormHasErrors(errors)">
                  There are errors in fields above.
                </span>
              </div>
              <SubmitButton
                class="rounded-md font-medium text-white bg-sky-600 px-3 py-2 hover:bg-blue-200 transition-colors shadow-sm"
                type="submit"
                color="blue"
                size="md"
                :disabled="!canSubmit"
                :submitting="submitting">
                Submit
              </SubmitButton>
            </div>
          </form>
        </ValidationObserver>
        <!-- <p>{{ data }}</p> -->
      </div>
    </Main>
    <div v-if="displayEndpoint">
      <p>endpoint data: {{ data }}</p>
    </div>
    <Modal ref="checkDateModal" size="sm" anchor="center" :onSuccess="handleSubmit">
      <template #title>Confirm date</template>
      <template #content>
        <div>
          {{ dateMessage }}
        </div>
      </template>
      <template #footer="slotProps">
        <button type="button"
          class="rounded-md font-medium text-green-900 bg-green-100 px-3 py-2 hover:bg-green-200 transition-colors shadow-sm"
          @click="() => slotProps.closeModal(true)">
          Confirm and Submit
        </button>
      </template>
    </Modal>
    <Modal ref="confirmDateModal" size="sm" anchor="center" :onSuccess="handleCheck">
      <template #title>Confirm date</template>
      <template #content>
        <div>
          Please confirm the following is correct.
        </div>
        <table>
          <tr>
            <th>
              {{ data.no_ride[0].multiple ? 'Start date:' : 'No ride date:' }}
            </th>
            <td>
              {{ data.no_ride[0].start_date }}
            </td>
          </tr>
          <tr v-if="data.no_ride[0].multiple">
            <th>
              End date:
            </th>
            <td>
              {{ data.no_ride[0].end_date }}
            </td>
          </tr>
        </table>
        <div>
          <b>Span: </b>{{ totalDays }} {{ data.no_ride[0].multiple ? 'days' : 'day' }}
        </div>
        <p></p>
        <div v-if="data.no_ride[0].multiple"><i>Notice: </i>Rides will be cancelled for all dates above.This is final and there are no One Time No Ride cancellations</div>
        <div v-else><i>Notice: </i>Rides will be cancelled for the date above. This is final and there are no One Time No Ride cancellations</div>
      </template>
      <template #footer="slotProps">
        <button type="button"
          class="rounded-md font-medium text-green-900 bg-green-100 px-3 py-2 hover:bg-green-200 transition-colors shadow-sm"
          @click="() => slotProps.closeModal(true)">
          Confirm and Submit
        </button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
</style>

<script>
import Main from '../components/Main.vue'
import Header from '../components/Header.vue'
import FormSection from '../components/FormSection.vue'
import Modal from '../components/Modal.vue'
import TextField from '../components/formComponents/TextField.vue'
import TimeField from '../components/formComponents/TimeField.vue'
import TextArea from '../components/formComponents/TextArea.vue'
import CalendarField from '../components/formComponents/CalendarField.vue'
import RadioField from '../components/formComponents/RadioField.vue'
import SubmitButton from '../components/SubmitButton.vue'

import oneTimeNoRideFormService from '../services/pages/oneTimeNoRideForm.js'

import '../services/validation.js'
import user from '../services/user/user.js'
import time from '../services/utils/time.js'

import DropdownLists from '../components/DropdownLists.json'


export default {
  props: {
    student: {}
  },
  components: {
    Main,
    Header,
    FormSection,
    Modal,
    TextField,
    TextArea,
    TimeField,
    CalendarField,
    SubmitButton,
    RadioField,
  },
  data() {
    return {
      displayEndpoint: process.env.NODE_ENV !== 'production',

      directionOptions: DropdownLists.directionList,
      user: {},
      data: {
        notifiedTime: undefined,
        notes: undefined,
        no_ride: [{ start_date: time.today(), end_date: null, direction: '', multiple: false }],
        requester: [{ first_name: '', last_name: '', relationship: '' }],
      },
      notifiedTimeRules: {
        'true': '',
        'false': 'required|beforeNowTime',
      },
      notifiedLastNightSelected: false,
      dateMessage: '',
      totalDays: 0,
      isInternal: false,
      canSubmit: true,
      submitting: false,
    }
  },
  computed: {
    minEndDate() {
      let date = this.data.no_ride?.[0].start_date
      if (!this.data) {
        date = time.today()
      }
      const startDate = new Date(date)
      let minEndDate = new Date(date)
      minEndDate.setDate(startDate.getDate() + 1)
      return minEndDate.toISOString().slice(0, 10)
    },
  },
  methods: {
    autofillUser(){
      this.data.requester[0].first_name = this.user.userData.first_name
      this.data.requester[0].last_name = this.user.userData.last_name
      let now = new Date(Date.now())
      this.data.notifiedTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    },
    onInputNotifiedLastNight() {
      this.notifiedLastNightSelected = !this.notifiedLastNightSelected

      console.log(this.notifiedTimeRules[this.notifiedLastNightSelected])

      if (this.notifiedLastNightSelected) {
        // let midnight = new Date()
        // midnight.setHours(0, 0, 0, 0)
        this.data.notifiedTime = undefined//'00:00'
      }
    },
    beforeStart() {
      let date = new Date(this.data.no_ride?.[0].start_date)
      let newDate = new Date(this.data.no_ride?.[0].end_date)
      return date.getTime() >= newDate.getTime()
    },
    isTwoWeeks() {
      let date = new Date(this.data.no_ride?.[0].start_date)
      date.setDate(date.getDate() + 14)
      let newDate = new Date(this.data.no_ride?.[0].end_date)
      return date.getTime() < newDate.getTime()
    },
    changeDirection(direction) {
      this.data.no_ride[0].direction = direction
    },
    toggleMultiple() {
      this.data.no_ride[0].multiple = !this.data.no_ride[0].multiple
      if (this.data.no_ride[0].end_date == null) {
        this.data.no_ride[0].end_date = this.getMinEndDate()
      }
      else { this.data.no_ride[0].end_date = null }
    },
    getTodayDate() {
      return time.today()
    },
    getMinEndDate() {
      return this.minEndDate
    },
    twoWeeksAhead() {
      let date = new Date(this.data.no_ride?.[0].start_date)
      date.setDate(date.getDate() + 14)
      return date.toISOString().slice(0, 10)
    },
    calculateTotalDays(){
      if (!this.data.no_ride[0].multiple) {
        return 1
      } else {
        let startDate = new Date(this.data.no_ride[0].start_date)
        let endDate = new Date(this.data.no_ride[0].end_date)
        let timeDiff = Math.abs(endDate.getTime() - startDate.getTime())
        return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1
      }
    },
    async updateUser() {
      this.user = await user.get()
      this.validateProvidedInfo()
      if(!this.isInternal){
        this.autofillUser()
      }
    },
    displayFormHasErrors(errorsMap) {
      return Object.values(errorsMap).flat().length > 0
    },
    scrollToFirstError() {
      const elems = document.getElementsByName('error-text');
      let hasError = false;

      for(let el of Array.from(elems)) {
        if (el.innerText && el.innerText.trim()) {
          hasError = true
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
          break
        }
      }

      return hasError
    },
    onSubmit(formInvalid) {
      let hasError = this.scrollToFirstError()

      if (formInvalid || hasError) {
        return false
      }
      if(!this.isInternal){
        this.totalDays = this.calculateTotalDays()
        this.$refs.confirmDateModal.openModal()
      }
      else {
        this.handleCheck()
      }
    },
    handleCheck() {
      const dateAfter = new Date()
      dateAfter.setDate(dateAfter.getDate() + 27)
      const startCheck = new Date(this.data.no_ride[0].start_date) > dateAfter
      const endCheck = new Date(this.data.no_ride[0].end_date) > dateAfter
      if (startCheck && endCheck) {
        this.dateMessage = 'Both the start and end dates are over a month later. Is this correct?'
        this.$refs.checkDateModal.openModal()
      } else if (startCheck) {
        this.dateMessage = 'The start date is over a month later. Is this correct?'
        this.$refs.checkDateModal.openModal()
      } else if (endCheck) {
        this.dateMessage = 'The end date is over a month later. Is this correct?'
        this.$refs.checkDateModal.openModal()
      } else {
        this.handleSubmit()
      }
    },
    async handleSubmit() {
      try {
        this.submitting = true
        const data = {
          student: this.student.dbId,
          requester_first_name: this.data.requester[0].first_name,
          requester_last_name: this.data.requester[0].last_name,
          requester_relationship: this.data.requester[0].relationship,
          start_date: this.data.no_ride[0].start_date,
          end_date: this.data.no_ride[0].end_date
            ? this.data.no_ride[0].end_date
            : null,
          direction: this.data.no_ride[0].direction,
          submit_user: this.user.getUserId(),
        }
        if (this.canSubmit) {
          if (!this.isInternal) {
            let now = new Date(Date.now())
            now.setHours(parseInt(this.data.notifiedTime.slice(0, 2)), parseInt(this.data.notifiedTime.slice(3, 5)), 0, 0)
            data.notified_time = now
          }
          else if (!this.notifiedLastNightSelected) {
            console.log(this.data.notifiedTime)
            let notifiedTime = new Date()
            notifiedTime.setHours(parseInt(this.data.notifiedTime.slice(0, 2)), parseInt(this.data.notifiedTime.slice(3, 5)), 0, 0)
            data.notified_time = notifiedTime
          }
          else {
            let midnight = new Date()
            midnight.setHours(0, 0, 0, 0)
            data.notified_time = midnight
          }
        }
        if (this.data.notes) {
          data.notes = this.data.notes
        }
        if (data.requester_relationship == undefined || data.requester_relationship == '') {
          data.requester_relationship = "CLIENT_STAFF"
        }

        await oneTimeNoRideFormService.postNewOneTimeNoRideForm(data)

        this.data = {}
        this.submitting = false

        this.$router.push('StudentList')
      }
      catch (error) {
        this.submitting = false
        console.log(error)
      }
    },
    validateProvidedInfo() {
      this.isInternal = oneTimeNoRideFormService.canViewInternalCstData(this.user)
      this.canSubmit = oneTimeNoRideFormService.canSubmitForm(this.user)
    },
  },
  mounted() {
    this.updateUser()
    if (this.student) {
      this.data.student = [
        {
          first_name: this.student.firstName,
          last_name: this.student.lastName,
          student_id: this.student.studentId
        }
      ]
    } else {
      this.$router.push('StudentList')
    }
  }
}
</script>

  